import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { DELETE } from "../tools/actions/ActionHelper";
import EventTypes from "../shared/EventTypes";
import { addSvg } from "../shared/utils";

export default class ElementRow extends AbstractProvidedClass {
    constructor(provider, element) {
        super(provider);

        this.element = element;

        this.row = this.p.createDiv()
            .parent(this.getTab('elements').rowContainer)
            .class('input-search-result')
            .mouseOver(() => this.mouseOver())
            .mouseOut(() => this.mouseOut())
            .mouseClicked(() => this.click());

        this.img = this.p.createElement('div')
            .parent(this.row)
            .class('data-img-xs align-center');
        this.label = this.p.createElement('label', '')
            .class('input-search-result-name align-center')
            .parent(this.row);

        this.element.row = this;
        this.updateImg();
        this.updateLabel();
        this.createOptions();

        this.on(EventTypes.UPDATE_ELEMENT_SHAPE, (element) => element == this.element ? this.updateImg() : null);
        this.on(EventTypes.UPDATE_ELEMENT_LABELS, (element) => element == this.element ?this.updateLabel() : null);
    }

    mouseOver() {
        if (this.touch) return;
        this.hoverSet.add(this.element);
        this.label.hide();
        this.options.show();
    }

    mouseOut() {
        if (this.touch) return;
        this.hoverSet.reset();
        this.label.show();
        this.options.hide();
    }

    updateImg() {
        let max = 37;
        this.img.html('');
        let svg = SVG().addTo(this.img.elt).size(40, 40);
        const scale = max / this.element.absMaxSize;
        switch (this.element.type) {
            case 'rectangle':
                let rect = svg.rect(
                    ...this.element.getMiniView(max)
                ).cx(20).cy(20);
                this.element.appearance.applyToSvg(rect, scale);
                break;
            case 'ellipse':
                let miniView = this.element.getMiniView(max);
                if (Array.isArray(miniView) && miniView.length === 2 && typeof miniView[0] === 'number') {
                    let ellipse = svg.ellipse(...miniView).cx(20).cy(20);
                    this.element.appearance.applyToSvg(ellipse, scale);
                } else if (Array.isArray(miniView)) {
                    let polygon = svg.polygon(miniView);
                    this.element.appearance.applyToSvg(polygon, scale);
                } else if (miniView?.type === 'conifer') {
                    // Fill circle behind needles (base size = absSize scaled to max)
                    if (this.element.appearance.fillColor) {
                        let baseSize = (this.element.absSize / this.element.absMaxSize) * max;
                        let circle = svg.circle(baseSize).cx(20).cy(20);
                        circle.fill({ color: this.element.appearance.fillColor.toString(), opacity: 200 / 255 });
                        circle.stroke('none');
                    }
                    let group = svg.group();
                    for (let line of miniView.lines) {
                        let l = group.line(line.x1, line.y1, line.x2, line.y2);
                        l.stroke({
                            color: this.strokeColor?.toString() ?? this.p.color(0).toString(),
                            width: this.strokeWidth ? Math.min(3, this.strokeWidth * scale * 0.5) : 0.15,
                        });
                    }
                }
                break;
            case 'line':
                let polyline = svg.polyline(
                    this.element.getMiniView(max)
                );
                this.element.appearance.applyToSvg(polyline, scale);
                break;
            case 'polygon':
                let polygon = svg.polygon(
                    this.element.getMiniView(max)
                );
                this.element.appearance.applyToSvg(polygon, scale);
                break;
            default:
        }
    }

    updateLabel() {
        this.label.html(`${this.element.name} <span>${this.element.secondaryName}</span>`);
    }

    click() {
        if (this.buttonClicked) {
            return;
        }
        this.hoverSet.reset();
        if (this.viewMode) {
            this.element.selected
                ? this.selection.remove(this.element)
                : this.selection.select(this.element);
        } else {
            this.tools.setTool('select');
            this.selection.click(this.element);
        }
    }

    createOptions() {
        this.options = this.p.createDiv()
            .parent(this.row);
        this.optionsContainer = this.p.createDiv()
            .parent(this.options)
            .class('row');

        this.options.hide();

        this.infoButton = this.createButton('Info', () => this.goToInfo(), 'information-line');
        if (this.editMode) {
            this.deleteButton = this.createButton('Delete', () => this.deleteElement(), 'close-circle-line');
        }

        if (this.touch) {
            if (this.viewMode) {
                // In view mode only the info button exists — show it directly
                this.label.hide();
                this.options.show();
            } else {
                this.moreButton = this.p.createDiv()
                    .parent(this.row)
                    .class('btn-icon')
                    .style('margin-left', 'auto')
                    .mouseClicked(() => this.toggleRowOptions());
                addSvg(this.moreButton, 'more-2-fill');
                this.on(EventTypes.CLOSE_SIDEBAR, () => this.closeRowOptions());
            }
        }
    }

    openRowOptions() {
        this.label.hide();
        this.moreButton.hide();
        this.options.show();
        this.hoverSet.add(this.element);
        this._closeHandler = (e) => {
            if (!this.row.elt.contains(e.target)) {
                this.closeRowOptions();
            }
        };
        setTimeout(() => document.addEventListener('touchstart', this._closeHandler), 0);
    }

    closeRowOptions() {
        if (this._closeHandler) {
            document.removeEventListener('touchstart', this._closeHandler);
            this._closeHandler = null;
        }
        this.label.show();
        this.moreButton.show();
        this.options.hide();
        this.hoverSet.reset();
    }

    toggleRowOptions() {
        if (this.options.elt.style.display === 'none') {
            this.openRowOptions();
        } else {
            this.closeRowOptions();
        }
    }

    createButton(toolTip, func, svgId) {
        let btn = this.p.createDiv()
            .parent(this.optionsContainer)
            .class('btn-icon')
            .attribute('data-tooltip', toolTip)
            .mouseClicked(func);
        let svg = SVG().addTo(btn.elt);
        svg.use(`${svgId}`, '/icons/__remixicon.sprite.svg?v=491');
        return btn;
    }

    goToInfo() {
        this.clickButton();
        this.selection.select(this.element);
        this.sideBar.setTab('info');
    }

    deleteElement() {
        this.clickButton();
        this.actions.execute(DELETE, [this.element]);
    }

    clickButton() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
    }

    delete() {
        this.row.remove();
    }

    show() {
        this.row.style('display', 'flex');
    }

    hide() {
        this.row.hide();
    }

    select() {
        this.row.style('background-color', '#aebbf240');
    }

    deselect() {
        this.row.style('background-color', null);
    }
}
